<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import router from '../../../../index';

export default {
  data() {
    return {
      selectedInboxId: null,
      isEnabling: false,
      isCheckingStatuses: false,
      callingStatuses: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      inboxes: 'inboxes/getInboxes',
      uiFlags: 'inboxes/getUIFlags',
    }),
    whatsappCloudInboxes() {
      return this.inboxes.filter(
        inbox =>
          inbox.channel_type === 'Channel::Whatsapp' &&
          inbox.provider === 'whatsapp_cloud'
      );
    },
    selectedInbox() {
      return this.whatsappCloudInboxes.find(
        inbox => inbox.id === this.selectedInboxId
      );
    },
    submitLabel() {
      return this.selectedInbox?.voice_enabled
        ? this.$t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.OPEN_SETTINGS')
        : this.$t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.ENABLE_BUTTON');
    },
  },
  async mounted() {
    await this.$store.dispatch('inboxes/get');
    await this.checkCallingStatuses();
  },
  methods: {
    selectInbox(inbox) {
      this.selectedInboxId = inbox.id;
    },
    statusFor(inbox) {
      return this.callingStatuses[inbox.id];
    },
    async checkCallingStatuses() {
      this.isCheckingStatuses = true;
      const statuses = await Promise.all(
        this.whatsappCloudInboxes.map(async inbox => {
          try {
            const response = await InboxesAPI.getWhatsappCallingStatus(
              inbox.id
            );
            return [inbox.id, response.data];
          } catch (error) {
            return [
              inbox.id,
              {
                status: 'UNAVAILABLE',
                error: error?.response?.data?.message,
              },
            ];
          }
        })
      );
      this.callingStatuses = Object.fromEntries(statuses);
      this.isCheckingStatuses = false;
    },
    openWhatsappSetup() {
      router.push({
        name: 'settings_inboxes_page_channel',
        params: { accountId: this.accountId, sub_page: 'whatsapp' },
      });
    },
    openInboxSettings() {
      router.replace({
        name: 'settings_inbox_show',
        params: {
          accountId: this.accountId,
          inboxId: this.selectedInbox.id,
        },
      });
    },
    async enableCalling() {
      if (!this.selectedInbox) return;

      if (this.selectedInbox.voice_enabled) {
        this.openInboxSettings();
        return;
      }

      this.isEnabling = true;
      try {
        await InboxesAPI.enableWhatsappCalling(this.selectedInbox.id);
        await this.$store.dispatch('inboxes/get');
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.SUCCESS'));
        this.openInboxSettings();
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            this.$t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE_FAILED')
        );
      } finally {
        this.isEnabling = false;
      }
    },
  },
};
</script>

<template>
  <woot-loading-state
    v-if="uiFlags.isFetching && !whatsappCloudInboxes.length"
    :message="$t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.LOADING')"
  />

  <div
    v-else-if="!whatsappCloudInboxes.length"
    class="max-w-2xl p-6 mt-6 border rounded-lg border-slate-200 dark:border-slate-700"
  >
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-100">
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.EMPTY_TITLE') }}
    </h3>
    <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.EMPTY_DESCRIPTION') }}
    </p>
    <woot-button class="mt-4" @click="openWhatsappSetup">
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.CONNECT_WHATSAPP') }}
    </woot-button>
  </div>

  <div v-else class="max-w-2xl mt-6">
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-100">
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.TITLE') }}
    </h3>
    <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.CREDENTIALS_NOTICE') }}
    </p>

    <div class="flex flex-col gap-3 mt-5">
      <button
        v-for="inbox in whatsappCloudInboxes"
        :key="inbox.id"
        type="button"
        class="flex items-center justify-between w-full gap-4 p-4 text-left border rounded-lg transition-colors"
        :class="
          selectedInboxId === inbox.id
            ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-woot-300'
        "
        @click="selectInbox(inbox)"
      >
        <span class="flex items-center gap-3 min-w-0">
          <input
            v-model="selectedInboxId"
            type="radio"
            name="whatsapp_call_inbox"
            :value="inbox.id"
            class="m-0"
            @click.stop
          />
          <span class="min-w-0">
            <span
              class="block font-medium truncate text-slate-900 dark:text-slate-100"
            >
              {{ inbox.name }}
            </span>
            <span class="block text-sm text-slate-600 dark:text-slate-300">
              {{ inbox.phone_number }}
            </span>
          </span>
        </span>

        <span class="flex flex-col items-end gap-1">
          <span
            v-if="inbox.voice_enabled"
            class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.CONNECTED') }}
          </span>
          <span
            v-else-if="statusFor(inbox)?.enabled"
            class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.META_ENABLED') }}
          </span>
          <span
            v-else-if="statusFor(inbox)?.status === 'DISABLED'"
            class="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.META_DISABLED') }}
          </span>
          <span
            v-else-if="statusFor(inbox)?.status === 'UNAVAILABLE'"
            class="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.CHECK_FAILED') }}
          </span>
        </span>
      </button>
    </div>

    <p
      v-if="isCheckingStatuses"
      class="mt-3 text-sm text-slate-500 dark:text-slate-400"
    >
      {{ $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.CHECKING') }}
    </p>

    <div
      v-if="selectedInbox"
      class="p-4 mt-5 text-sm rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
    >
      {{
        $t('INBOX_MGMT.WHATSAPP_CALLING.SELECTOR.REUSE_NOTICE', {
          inboxName: selectedInbox.name,
        })
      }}
    </div>

    <woot-button
      class="mt-5"
      :disabled="!selectedInbox"
      :is-loading="isEnabling"
      @click="enableCalling"
    >
      {{ submitLabel }}
    </woot-button>
  </div>
</template>
